import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";

import { cn } from "../utils";
import { badgeVariants } from "./badge-variants";

export type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>["size"]>;

/**
 * The size a `Badge` takes when its caller doesn't pass one. Dense surfaces
 * (a `ResourceList` row's status slot) provide `sm` so every status badge in
 * them shrinks without each caller opting in.
 */
const BadgeSizeContext = React.createContext<BadgeSize>("default");

export interface BadgeProps
  extends React.ComponentProps<"span">, VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

function Badge({ className, variant, size, asChild = false, ...props }: BadgeProps) {
  const contextSize = React.useContext(BadgeSizeContext);
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size: size ?? contextSize }), className)}
      {...props}
    />
  );
}

export { Badge, BadgeSizeContext };
